import { Reveal } from '@/components/utils/RevealOnScroll';

export interface MoatItem {
  headline: string;
  body: string;
}

interface MoatBlockProps {
  eyebrow?: string;
  title: string;
  lede?: string;
  items: MoatItem[];
}

/**
 * "Why teams stall when they build this themselves" — PRD.md §1.4.
 * Four items, numbered. Used on /orchestration (developer voice) and
 * /personalization (enterprise voice). Same shape, different content.
 */
export function MoatBlock({ eyebrow, title, lede, items }: MoatBlockProps) {
  return (
    <section
      className="section"
      style={{ borderTop: '1px solid var(--border-subtle)' }}
    >
      <div className="container">
        <Reveal>
          {eyebrow && (
            <div
              className="text-eyebrow"
              style={{ marginBottom: 12, color: 'var(--accent)' }}
            >
              {eyebrow}
            </div>
          )}
          <h2 className="text-h2" style={{ maxWidth: 820 }}>
            {title}
          </h2>
          {lede && (
            <p
              className="text-body-lg"
              style={{
                marginTop: 20,
                maxWidth: 760,
                color: 'var(--ink-secondary)',
              }}
            >
              {lede}
            </p>
          )}
        </Reveal>
        <ol
          className="moat-grid"
          style={{
            marginTop: 56,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 16,
            listStyle: 'none',
            padding: 0,
          }}
        >
          {items.map((item, i) => (
            <Reveal key={item.headline} delay={i * 80}>
              <li
                className="glass glass--settled"
                style={{
                  padding: 28,
                  height: '100%',
                  borderRadius: 12,
                }}
              >
                <div
                  className="text-mono"
                  style={{
                    fontSize: 12,
                    color: 'var(--accent)',
                    letterSpacing: '0.06em',
                    marginBottom: 16,
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3
                  className="text-h4"
                  style={{ marginBottom: 12, color: 'var(--ink-primary)' }}
                >
                  {item.headline}
                </h3>
                <p
                  style={{
                    color: 'var(--ink-secondary)',
                    fontSize: 15,
                    lineHeight: 1.65,
                  }}
                >
                  {item.body}
                </p>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
      <style>{`
        @media (max-width: 760px) {
          .moat-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}
